'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function GravelCalculator() {
  const t = useTranslations("tools.gravelCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [areaType, setAreaType] = useState('total');
  const [totalArea, setTotalArea] = useState(200);
  const [totalAreaUnit, setTotalAreaUnit] = useState('sqft');
  const [rectLength, setRectLength] = useState(20);
  const [rectLengthUnit, setRectLengthUnit] = useState('ft');
  const [rectWidth, setRectWidth] = useState(15);
  const [rectWidthUnit, setRectWidthUnit] = useState('ft');
  const [circleDiameter, setCircleDiameter] = useState(10);
  const [circleDiameterUnit, setCircleDiameterUnit] = useState('ft');
  const [depth, setDepth] = useState(3);
  const [depthUnit, setDepthUnit] = useState('in');
  const [densityType, setDensityType] = useState('1600');
  const [customDensity, setCustomDensity] = useState(1600);
  const [densityUnit, setDensityUnit] = useState('kgm3');
  const [price, setPrice] = useState(0);
  const [priceUnit, setPriceUnit] = useState('cuft');
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const conversionFactors = {
    length: {
      ft: 1,
      m: 3.28084,
      in: 0.0833333,
      cm: 0.0328084,
      yd: 3
    },
    area: {
      sqft: 1,
      sqm: 10.7639,
      sqyd: 9,
      acre: 43560
    },
    volume: {
      cuft: 1,
      cuyd: 27,
      cum: 35.3147
    },
    density: {
      kgm3: 1,
      gcm3: 1000,
      lbft3: 16.0185
    },
    weight: {
      kg: 1,
      lb: 0.453592,
      ton: 907.185
    }
  };

  const convertToFeet = (value: number, unit: string) => {
    return value * (conversionFactors.length as any)[unit];
  };

  const convertToSqFeet = (value: number, unit: string) => {
    return value * (conversionFactors.area as any)[unit];
  };

  const getDensityInKgM3 = () => {
    if (densityType === 'custom') {
      if (densityUnit === 'kgm3') return customDensity;
      if (densityUnit === 'gcm3') return customDensity * 1000;
      if (densityUnit === 'lbft3') return customDensity * 16.0185;
    }
    return parseFloat(densityType);
  };

  const calculateArea = () => {
    if (areaType === 'total') {
      return convertToSqFeet(totalArea, totalAreaUnit);
    } else if (areaType === 'rectangle') {
      const lengthFt = convertToFeet(rectLength, rectLengthUnit);
      const widthFt = convertToFeet(rectWidth, rectWidthUnit);
      return lengthFt * widthFt;
    } else if (areaType === 'circle') {
      const diameterFt = convertToFeet(circleDiameter, circleDiameterUnit);
      const radius = diameterFt / 2;
      return Math.PI * radius * radius;
    }
    return 0;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const areaSqFt = calculateArea();
    const depthFt = convertToFeet(depth, depthUnit);

    const volumeCuFt = areaSqFt * depthFt;
    const volumeCuYd = volumeCuFt / 27;
    const volumeCuM = volumeCuFt / 35.3147;

    const densityKgM3 = getDensityInKgM3();
    const weightKg = volumeCuM * densityKgM3;
    const weightLb = weightKg * 2.20462;
    const weightTon = weightLb / 2000;

    let totalCost = 0;
    if (price > 0) {
      switch(priceUnit) {
        case 'cuft':
          totalCost = price * volumeCuFt;
          break;
        case 'cuyd':
          totalCost = price * volumeCuYd;
          break;
        case 'cum':
          totalCost = price * volumeCuM;
          break;
        case 'ton':
          totalCost = price * weightTon;
          break;
        case 'kg':
          totalCost = price * weightKg;
          break;
        case 'lb':
          totalCost = price * weightLb;
          break;
      }
    }

    setResults({
      volumeCuFt,
      volumeCuYd,
      volumeCuM,
      weightTon,
      weightLb,
      weightKg,
      areaSqFt,
      totalCost
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', textAlign: 'center' as const },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const toolH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formSectionStyle = getResponsiveStyle(
    { background: '#f8fafc', borderRadius: '0.75rem', marginBottom: '1.5rem', border: '2px solid #e2e8f0' },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const formSectionTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#1e293b', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const formGroupStyle = { marginBottom: '1.25rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const radioGroupStyle = { display: 'flex', flexDirection: 'column' as const, gap: '0.75rem', marginBottom: '1rem' };

  const radioOptionStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' };

  const radioInputStyle = { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2563eb' };

  const radioLabelStyle = { margin: 0, cursor: 'pointer', fontWeight: 500 };

  const inputRowStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.75rem', alignItems: 'center' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const inputStyle = getResponsiveStyle(
    { flex: 1, padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '1rem', transition: 'all 0.3s ease', width: '100%' },
    {},
    {},
    {}
  );

  const selectStyle = getResponsiveStyle(
    { padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '1rem', background: 'white', cursor: 'pointer', transition: 'all 0.3s ease' },
    { width: '100%' },
    { minWidth: '150px', width: 'auto' },
    { minWidth: '150px', width: 'auto' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    { width: '100%', background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', border: 'none', borderRadius: '0.75rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s ease', marginTop: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderTop: '3px solid #22c55e' },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#166534', marginTop: 0, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const resultItemStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.75rem', borderLeft: '4px solid #2563eb', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle = { display: 'block', fontSize: '0.9rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 500 };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#1e293b' },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const costResultStyle = { ...resultItemStyle, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderLeftColor: '#f59e0b', marginTop: '1rem' };

  const costResultValueStyle = { ...resultValueStyle, color: '#92400e' };

  const glassCardStyle = getResponsiveStyle(
    { background: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', border: '1px solid rgba(255, 200, 180, 0.2)', borderRadius: '1rem', boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)', marginBottom: '2rem' },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const insightCardStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, rgba(255, 247, 237, 0.9) 0%, rgba(254, 243, 199, 0.9) 100%)', border: '2px solid rgba(251, 191, 36, 0.3)', borderRadius: '0.75rem', marginBottom: '1rem' },
    { padding: '1rem' },
    { padding: '1.25rem', borderRadius: '1rem' },
    { padding: '1.5rem', borderRadius: '1.25rem' }
  );

  const insightTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#92400e', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const insightTextStyle = getResponsiveStyle(
    { color: '#78350f', lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    { background: 'rgba(255, 255, 255, 0.8)', borderRadius: '0.75rem', marginBottom: '1rem', border: '1px solid rgba(255, 200, 180, 0.2)' },
    { padding: '1.25rem' },
    { padding: '1.375rem', borderRadius: '1rem' },
    { padding: '1.5rem', borderRadius: '1.25rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const referenceStyle = getResponsiveStyle(
    { background: 'rgba(255, 255, 255, 0.5)', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '0.75rem', border: '1px solid rgba(255, 200, 180, 0.15)' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem', padding: '0.875rem', borderRadius: '0.625rem' },
    { fontSize: '1rem', padding: '1rem', borderRadius: '0.75rem' }
  );

  const linkStyle = { color: colors.primary, textDecoration: 'none', fontWeight: 500 };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolH2Style}>🪨 {t("toolTitle")}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>📏 {t("areaToCover")}</div>

              <div style={radioGroupStyle}>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="area-total"
                    name="areaType"
                    value="total"
                    checked={areaType === 'total'}
                    onChange={(e) => setAreaType(e.target.value)}
                    style={radioInputStyle}
                  />
                  <label htmlFor="area-total" style={radioLabelStyle}>{t("useTotalArea")}</label>
                </div>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="area-rectangle"
                    name="areaType"
                    value="rectangle"
                    checked={areaType === 'rectangle'}
                    onChange={(e) => setAreaType(e.target.value)}
                    style={radioInputStyle}
                  />
                  <label htmlFor="area-rectangle" style={radioLabelStyle}>{t("rectangle")}</label>
                </div>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="area-circle"
                    name="areaType"
                    value="circle"
                    checked={areaType === 'circle'}
                    onChange={(e) => setAreaType(e.target.value)}
                    style={radioInputStyle}
                  />
                  <label htmlFor="area-circle" style={radioLabelStyle}>{t("circle")}</label>
                </div>
              </div>

              {areaType === 'total' && (
                <div style={formGroupStyle}>
                  <label htmlFor="total-area" style={labelStyle}>{t("totalArea")}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      id="total-area"
                      step="0.01"
                      min="0"
                      value={totalArea}
                      onChange={(e) => setTotalArea(parseFloat(e.target.value))}
                      style={inputStyle}
                    />
                    <select
                      id="total-area-unit"
                      value={totalAreaUnit}
                      onChange={(e) => setTotalAreaUnit(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="sqft">{t("squareFeet")}</option>
                      <option value="sqm">{t("squareMeters")}</option>
                      <option value="sqyd">{t("squareYards")}</option>
                      <option value="acre">{t("acres")}</option>
                    </select>
                  </div>
                </div>
              )}

              {areaType === 'rectangle' && (
                <>
                  <div style={formGroupStyle}>
                    <label htmlFor="rect-length" style={labelStyle}>{t("length")}</label>
                    <div style={inputRowStyle}>
                      <input
                        type="number"
                        id="rect-length"
                        step="0.01"
                        min="0"
                        value={rectLength}
                        onChange={(e) => setRectLength(parseFloat(e.target.value))}
                        style={inputStyle}
                      />
                      <select
                        id="rect-length-unit"
                        value={rectLengthUnit}
                        onChange={(e) => setRectLengthUnit(e.target.value)}
                        style={selectStyle}
                      >
                        <option value="ft">{t("feet")}</option>
                        <option value="m">{t("meters")}</option>
                        <option value="in">{t("inches")}</option>
                        <option value="cm">{t("centimeters")}</option>
                        <option value="yd">{t("yards")}</option>
                      </select>
                    </div>
                  </div>
                  <div style={formGroupStyle}>
                    <label htmlFor="rect-width" style={labelStyle}>{t("width")}</label>
                    <div style={inputRowStyle}>
                      <input
                        type="number"
                        id="rect-width"
                        step="0.01"
                        min="0"
                        value={rectWidth}
                        onChange={(e) => setRectWidth(parseFloat(e.target.value))}
                        style={inputStyle}
                      />
                      <select
                        id="rect-width-unit"
                        value={rectWidthUnit}
                        onChange={(e) => setRectWidthUnit(e.target.value)}
                        style={selectStyle}
                      >
                        <option value="ft">{t("feet")}</option>
                        <option value="m">{t("meters")}</option>
                        <option value="in">{t("inches")}</option>
                        <option value="cm">{t("centimeters")}</option>
                        <option value="yd">{t("yards")}</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {areaType === 'circle' && (
                <div style={formGroupStyle}>
                  <label htmlFor="circle-diameter" style={labelStyle}>{t("diameter")}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      id="circle-diameter"
                      step="0.01"
                      min="0"
                      value={circleDiameter}
                      onChange={(e) => setCircleDiameter(parseFloat(e.target.value))}
                      style={inputStyle}
                    />
                    <select
                      id="circle-diameter-unit"
                      value={circleDiameterUnit}
                      onChange={(e) => setCircleDiameterUnit(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="ft">{t("feet")}</option>
                      <option value="m">{t("meters")}</option>
                      <option value="in">{t("inches")}</option>
                      <option value="cm">{t("centimeters")}</option>
                      <option value="yd">{t("yards")}</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>📐 {t("gravelDepth")}</div>
              <div style={formGroupStyle}>
                <label htmlFor="depth" style={labelStyle}>{t("depth")}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    id="depth"
                    step="0.01"
                    min="0"
                    value={depth}
                    onChange={(e) => setDepth(parseFloat(e.target.value))}
                    required
                    style={inputStyle}
                  />
                  <select
                    id="depth-unit"
                    value={depthUnit}
                    onChange={(e) => setDepthUnit(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="in">{t("inches")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="m">{t("meters")}</option>
                    <option value="yd">{t("yards")}</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>⚖️ {t("gravelDensity")}</div>
              <div style={formGroupStyle}>
                <label htmlFor="density-type" style={labelStyle}>{t("materialType")}</label>
                <select
                  id="density-type"
                  value={densityType}
                  onChange={(e) => setDensityType(e.target.value)}
                  style={selectStyle}
                >
                  <option value="1600">{t("gravelStandard")}</option>
                  <option value="1750">{t("gravelWithSand")}</option>
                  <option value="1520">{t("drySand")}</option>
                  <option value="1920">{t("wetSand")}</option>
                  <option value="custom">{t("customDensity")}</option>
                </select>
              </div>
              {densityType === 'custom' && (
                <div style={formGroupStyle}>
                  <label htmlFor="custom-density" style={labelStyle}>{t("customDensityLabel")}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      id="custom-density"
                      step="0.01"
                      min="0"
                      value={customDensity}
                      onChange={(e) => setCustomDensity(parseFloat(e.target.value))}
                      style={inputStyle}
                    />
                    <select
                      id="density-unit"
                      value={densityUnit}
                      onChange={(e) => setDensityUnit(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="kgm3">{t("kgm3")}</option>
                      <option value="gcm3">{t("gcm3")}</option>
                      <option value="lbft3">{t("lbft3")}</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>💰 {t("priceOptional")}</div>
              <div style={formGroupStyle}>
                <label htmlFor="price" style={labelStyle}>{t("pricePerUnit")}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    id="price"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={price || ''}
                    onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    id="price-unit"
                    value={priceUnit}
                    onChange={(e) => setPriceUnit(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="cuft">{t("perCubicFoot")}</option>
                    <option value="cuyd">{t("perCubicYard")}</option>
                    <option value="cum">{t("perCubicMeter")}</option>
                    <option value="ton">{t("perTon")}</option>
                    <option value="kg">{t("perKilogram")}</option>
                    <option value="lb">{t("perPound")}</option>
                  </select>
                </div>
              </div>
            </div>

            <button type="submit" style={calculateBtnStyle}>{t("calculateButton")}</button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t("results")}</h3>
              <div style={resultsGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("volumeNeeded")}</span>
                  <span style={resultValueHighlightStyle}>
                    {results.volumeCuFt.toFixed(2)} ft³ ({results.volumeCuYd.toFixed(2)} yd³)
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("weightNeeded")}</span>
                  <span style={resultValueStyle}>
                    {results.weightTon.toFixed(2)} tons ({results.weightLb.toFixed(0)} lbs)
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("areaCovered")}</span>
                  <span style={resultValueStyle}>
                    {results.areaSqFt.toFixed(2)} ft²
                  </span>
                </div>
              </div>
              {results.totalCost > 0 && (
                <div style={costResultStyle}>
                  <span style={resultLabelStyle}>💵 {t("estimatedTotalCost")}</span>
                  <span style={costResultValueStyle}>
                    ${results.totalCost.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li>{t("howToStep1")}</li>
          <li>{t("howToStep2")}</li>
          <li>{t("howToStep3")}</li>
          <li>{t("howToStep4")}</li>
          <li>{t("howToStep5")}</li>
          <li>{t("howToStep6")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>💡 {t("insight1Title")}</h3>
          <p style={insightTextStyle}>{t("insight1Text")}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>🎯 {t("insight2Title")}</h3>
          <p style={insightTextStyle}>{t("insight2Text")}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>📊 {t("insight3Title")}</h3>
          <p style={insightTextStyle}>{t("insight3Text")}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>⚠️ {t("insight4Title")}</h3>
          <p style={insightTextStyle}>{t("insight4Text")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>
        <p style={paragraphStyle}>{t("detailsParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <div style={referenceStyle}>
          <a href="https://braensupply.com/gravel-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference1")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://stonecenters.com/gravel-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference2")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://calculatorgravel.com" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference3")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.omnicalculator.com/construction/gravel" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference4")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.calculator.net/gravel-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference5")}
          </a>
        </div>
      </section>
    </div>
  );
}